import React, { memo, useRef, useLayoutEffect } from 'react'
import PropTypes from 'prop-types'

/**
 * DateTimePickerDay component displays a single day in a date picker.
 * It shows the date and optionally displays availability information.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Date} props.day - The date to display.
 * @param {('available'|'unavailable'|'partial')} [props.available] - The availability status of the day.
 * @param {number} [props.availableCount] - The count of available slots for the day.
 * @param {(true|false|'count')} [props.showAvailability] - Whether and how to show availability.
 * @param {boolean} [props.showCounts] - Whether to show the availability count.
 * @param {(string|Object)} [props.modifiers] - Modifiers for the day (e.g., 'past', 'future').
 * @param {boolean} [props.showPast] - Whether to show availability for past dates.
 * @returns {React.ReactElement} The rendered DateTimePickerDay component.
 */
const DateTimePickerDay = memo(
  ({ day, isMobile, available, availableCount, showAvailability, showCounts, modifiers, showPast }) => {
    const ref = useRef(null)

    useLayoutEffect(() => {
      if (!isMobile || modifiers?.from !== true) return

      ref.current?.scrollIntoView({ block: 'center', behavior: 'instant' })
    }, [])

    const className = [
      'DayPicker-Day__wrapper',
      ...(!!showAvailability && (showPast || !modifiers?.past) ?
        ['DayPicker-Day__availability', available].filter(Boolean) :
        [])
    ].join(' ')

    return (
      <div ref={ref} className={className}>
        <div className="DayPicker-Day__inner">
          <div className="DayPicker-Day__date">{day.getDate()}</div>
          {!!showAvailability && (showPast || !modifiers?.past) && showCounts && (
            <div className="DayPicker-Day__availability-count">
              {typeof availableCount === 'number' ? availableCount : '...'}
            </div>
          )}
        </div>
      </div>
    )
  },
  (prevProps, nextProps) => {
    // Returning prop comparison to understand if the component should be re-rendered
    // true - component should not be re-rendered
    // false - component should be re-rendered
    return (
      prevProps.available === nextProps.available &&
      prevProps.day.getTime() === nextProps.day.getTime() &&
      prevProps.modifiers === nextProps.modifiers
    )
  }
)

DateTimePickerDay.displayName = 'DateTimePickerDay'

DateTimePickerDay.propTypes = {
  /** The date to display */
  day: PropTypes.instanceOf(Date).isRequired,
  /** The availability status of the day */
  available: PropTypes.oneOf(['available', 'unavailable', 'partial']),
  /** The count of available slots for the day */
  availableCount: PropTypes.number,
  /** Whether and how to show availability */
  showAvailability: PropTypes.oneOf([true, false, 'count']),
  /** Whether to show the availability count */
  showCounts: PropTypes.bool,
  /** Modifiers for the day (e.g., 'past', 'future') */
  modifiers: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Whether to show availability for past dates */
  showPast: PropTypes.bool,
  /** Whether the day is displayed on a mobile device */
  isMobile: PropTypes.bool
}

export default DateTimePickerDay
